/**
 * Header carrying the Cloudflare Turnstile token from the browser through the
 * BFF to the Hub (AP-11097, AP-11102).
 */
export const CAPTCHA_TOKEN_HEADER = 'cf-turnstile-response'

// The Hub's 400 `error` values for a captcha rejection. Used to detect the
// state only; the text shown to visitors lives in the frontend copy.
const HUB_CAPTCHA_ERRORS = new Set([
  'Missing captcha token.',
  'Invalid captcha token.',
  'Expired captcha token.',
  'Captcha verification failed. Please try again.',
])

const hubErrorOf = (value: unknown): unknown =>
  (value as { error?: unknown } | null | undefined)?.error

/**
 * Whether a failed request was rejected by the Hub's captcha check. Accepts
 * either the upstream body (`{ error }`) or a BFF error, which nests it under
 * `data` (`{ data: { error } }`) or `data.data` on the client.
 */
export const isCaptchaRejection = (
  status: number | undefined,
  body: unknown
) => {
  if (status !== 400) return false

  const nested = (body as { data?: unknown } | null | undefined)?.data
  const candidates = [
    hubErrorOf(body),
    hubErrorOf(nested),
    hubErrorOf((nested as { data?: unknown } | null | undefined)?.data),
  ]

  return candidates.some(
    (candidate) =>
      typeof candidate === 'string' && HUB_CAPTCHA_ERRORS.has(candidate)
  )
}

/**
 * Headers to attach a token to an outgoing request, or none when there is no
 * token (widget not configured or not ready), so the Hub decides per endpoint.
 */
export const captchaHeaders = (
  token: string | null | undefined
): Record<string, string> => (token ? { [CAPTCHA_TOKEN_HEADER]: token } : {})
